import React from "react";
import { Col, FormGroup } from "reactstrap";
import VariableField, { type VariableFieldProps } from "../../variableFields/VariableField";

/**
 * ContentColumn — one column of template content variables in the page-CREATE
 * wizard (SCRUM-908). IndexTemplateSelector renders it twice (ContentRequired /
 * ContentOptional); the columns differ only in their override map and a couple
 * of per-column fieldProps, which stay DATA passed in by the host.
 *
 * The variables appear in template order. The columns were drag-orderable until
 * SCRUM-2187; the dragged order did not survive the first edit, because the
 * update screen rebuilds the content in template order.
 *
 * Each row carries `data-testid="content-block-<variable>"`, which the e2e page
 * object uses to scope a variable's inputs.
 *
 * Focus retention: the host passes STABLE (module-scope) `overrides` maps; this
 * component never creates block identities, so the subtree is not remounted on
 * re-render.
 */
export type ContentColumnProps<T extends { variable?: string }> = {
  /** FormGroup className (column width). */
  className: string;
  /** Already-filtered content variables. */
  items: T[];
  overrides: VariableFieldProps["overrides"];
  optionsFor: (cv: T) => unknown;
  onChange: (name: string, value: unknown) => void;
  fieldPropsFor: (cv: T) => VariableFieldProps["fieldProps"];
  errorFor: (cv: T) => VariableFieldProps["error"];
};

export default function ContentColumn<T extends { variable?: string }>({
  className,
  items,
  overrides,
  optionsFor,
  onChange,
  fieldPropsFor,
  errorFor,
}: ContentColumnProps<T>) {
  return (
    <FormGroup className={className}>
      <Col>
        {items.map((contentVar) => (
          <div key={contentVar.variable} data-testid={`content-block-${contentVar.variable}`}>
            <VariableField
              settingVar={contentVar}
              options={optionsFor(contentVar)}
              onChange={onChange}
              overrides={overrides}
              fieldProps={fieldPropsFor(contentVar)}
              error={errorFor(contentVar)}
            />
          </div>
        ))}
      </Col>
    </FormGroup>
  );
}
